/* The Learn tab's persisted-state door: load/save the single localStorage
 * blob (XP, streak, badges, Leitner cards, settings). Split out of store.js
 * so main-chunk consumers (App's Settings modal, ProModal) can read/write
 * settings WITHOUT dragging the whole course catalog (courses.data +
 * generated lines/ideas, ~hundreds of KB) into the initial bundle —
 * store.js's derivation helpers import the catalog; this file must not. */

import { migrateCardIds } from "./coachingRecord.js";

const KEY = "towardsgm:learn";

export function emptyState() {
  return {
    xp: 0,
    correctTotal: 0,
    /* T2b · the rank ladder's high-water mark, in points (a learned line is
     * 1, a mastered one 2 — see rank.js). The ladder itself stays DERIVED;
     * this is stored only so a miss, which sets a card back to box 0 and so
     * un-learns its line, can never pull the number backwards. Max-merged
     * across devices like XP and Leitner boxes. */
    bestKnown: 0,
    /* F4 · `freezes` is a banked count of streak saves, `freezeWeek` the most
     * recent week (Monday's dayKey) one was granted for — see
     * goals.js#grantWeeklyFreeze and store.js#bumpStreak for how each is
     * spent and earned. A legacy blob has neither; every read site defaults
     * them (`|| 0` / `|| null`) rather than assuming this shape. */
    streak: { count: 0, lastDay: null, freezes: 0, freezeWeek: null },
    badges: [],
    cards: {}, // cardId -> { box, dueAt, hits, lapses }
    courses: {}, // courseId -> { startedAt }
    settings: {
      showLegalMoves: true, // toggle: highlight legal destinations while drilling
      sound: true, // toggle: move/feedback sound effects
      boardTheme: "walnut", // key into theme.BOARD_PRESETS
      pieceSet: "filled", // key into theme.PIECE_SETS
      /* Key into i18n.js#SUPPORTED_LOCALES, or null until the reader picks
       * one in Settings: i18n.js#initLocale then follows the device's
       * language. A default of "en" here hid the device's language from every
       * new visitor, since an unchosen "en" can't be told from a chosen one. */
      locale: null,
      /* M1a · show moves as ♞f3 rather than Nf3. Default ON for everyone
       * (Pablo, 2026-08-17): it is what lichess does, it is more readable at
       * a glance, and it removes a whole class of i18n bug, since SAN piece
       * letters are language-specific. Stored explicitly rather than left
       * undefined so the Settings checkbox reflects the real behaviour. */
      figurineNotation: true,
      seenCoursePathNudge: false, // one-time "your path resumes here" pointer
      /* Explanations the user has waved away, by note id. One shared map
       * rather than a flag per note, so adding a dismissible note costs a
       * string and not a state migration. Unioned across devices in sync.js —
       * dismissing something is progress; having it come back is not. */
      dismissed: {},
    },

    /* ---- reserved slots (product_improvements_v1.md, Phase 0) ----
     * Declared here ahead of the features that fill them, on purpose: four
     * separate workstreams need state, and each one editing this file and
     * sync.js is a merge conflict per branch per rebase. Unused keys cost a
     * few bytes; racing on the same two files costs a week. A slot stays
     * null/{} until its workstream lands — nothing reads them yet.
     *
     * Their merge rules live in sync.js#mergeLearn and are the part that
     * actually matters: these keys do NOT all inherit the never-lose-progress
     * max/union semantics that XP, Leitner boxes and badges use. See the
     * comments there before changing a shape below. */

    /* A · the profile the user DECLARES in the onboarding wizard, as opposed
     * to the one profile.js mines from game history. Merged with the mined
     * one by resolveProfile(); this is what lets a user with no Lichess
     * account get personalization on day one.
     * { declaredAt, completedAt, playsOnline: "lichess"|"chesscom"|"both"|"no",
     *   ratingBand: "u800"|"800-1200"|"1200-1600"|"1600-2000"|"2000+"|"unsure",
     *   experience, styleTags: [] (the catalog's OWN taxonomy, never a
     *   parallel one), repertoire: { white, black } } */
    profile: null,

    /* F · the training goal the user CHOOSES (a commitment device — the app
     * asks, it does not assign). { updatedAt, perWeek, remindAt: "19:30",
     * remindersOn } */
    goals: null,

    /* B · the last coach briefing, kept so it is stable across reloads and
     * consistent across devices — a briefing that reshuffles on every render
     * is not a coach. Carries the cards it targeted so the NEXT briefing can
     * open with the verdict on them (B4, the follow-up arc).
     * { generatedAt, headline, findings: [], cardIds: [], targeted: [] } */
    briefing: null,

    /* G3 · lineKey ("courseId:lineId") -> the fastest clean mastery run.
     * Mastery mode has always timed the run and compared it to nothing, so
     * the mode built to be replayed gave you no reason to replay it.
     * { ms, at, perfect } — and note its merge rule INVERTS the usual one:
     * a best is the SMALLER number. See sync.js. */
    bests: {},

    /* E · cardId -> the coaching card as a RECORD (raised on a date, worked
     * on, resolved) rather than today's transient query result, which
     * silently vanishes the moment the user fixes the habit.
     * { raisedAt, updatedAt, status: "active"|"improving"|"fixed"|"stale",
     *   fen, context, orientation, habit: { san, rate, share },
     *   expected: { san, rate, share }, costCp, gamesSeen, fixedAt,
     *   timesFixed } — see coachingRecord.js for the transitions.
     * The reserved shape also listed a "retired" status; nothing sets one.
     * Going over RECORD_CAP drops the longest-RESOLVED records instead, and
     * never an active leak, which is the same intent without a status that
     * only ever describes a record you can no longer read. */
    coachingRecord: {},

    /* G7 · which weekly recap has already been shown. Just a marker, not the
     * recap itself: the card is derived from the games cache and the week
     * strip on every render, so storing it would be a second copy of numbers
     * that can be recomputed exactly — and one that could disagree.
     * { weekKey, dismissedAt } */
    recap: null,

    /* C4 · the Rival ladder — per course, the highest opponent rating beaten
     * in FreePlay/Game-on. courseId -> { bestBeaten, at }. `bestBeaten` is a
     * raw rating from engine.js#OPPONENT_RATINGS (a number, or `null` for
     * "Full strength" — the TOP of the ladder, not a missing value), so it
     * needs no translation to render next to the same picker that produced
     * it. See rivalLadder.js for why comparisons go through that list's
     * order rather than `>`. */
    rivalLadder: {},

    /* C5 · drill cards minted straight from a Rival loss (see rivalCards.js).
     * `rival:<4-field FEN>` -> { fen, context, orientation, expected, played,
     * costCp, courseId, raisedAt }. Deliberately no lifecycle (no
     * active/improving/fixed like coachingRecord) — these are single-game
     * leaks, not population-mined habits, and Pablo asked (2026-08-10) for
     * the lighter mechanism rather than folding Rival play into the miner. */
    rivalCards: {},
  };
}

export function loadLearn() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY));
    if (!raw) return emptyState();
    const base = emptyState();
    return {
      ...base,
      ...raw,
      settings: { ...base.settings, ...raw.settings, dismissed: raw.settings?.dismissed || {} },
      // The map-shaped keys have to survive a blob written before they
      // existed, or one where a key round-tripped as null: the spread above
      // would hand callers a null exactly where they iterate. Every stored
      // blob predates `coachingRecord` and `bests`, so this is their normal
      // path, not an edge case.
      /* E1's migration runs at the door, not at a call site, because there
       * are two doors — this one and sync.js's merge — and a legacy id that
       * slips past either is Leitner progress orphaned forever. Cheap: a pass
       * over a few hundred keys, and a no-op once nothing legacy is left. */
      cards: migrateCardIds(raw.cards || base.cards).cards,
      courses: raw.courses || base.courses,
      bests: raw.bests || base.bests,
      coachingRecord: raw.coachingRecord || base.coachingRecord,
    };
  } catch {
    return emptyState();
  }
}

/* Has this explanation been waved away? Read at every render site of a
 * dismissible note, so it tolerates a blob written before `dismissed` existed
 * rather than assuming loadLearn ran (sync's merge output also lands here). */
export const isDismissed = (ls, id) => Boolean(ls?.settings?.dismissed?.[id]);

export function saveLearn(state) {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    /* private mode etc. — progress just won't persist */
  }
}
